/**
 * PHARMAKON — ordonnancier v2: the SPA twin of the server CONFIG
 * `apps/server/src/config/ordonnancier-dispensation.config.js` (cross-module X XWP-54 server, XWP-55 screens;
 * CP §15.13, §15.14, §15.21-9; XO-08, XO-09; XA-32; defaults XD-272…XD-277). The server derives the
 * attachment kinds from the Prisma enum `TypePieceOrdonnancier` and its file rules from the clinical byte
 * path (`config/document-store.config.js`); the browser cannot import Prisma, so this file repeats them
 * once — `apps/server/src/__tests__/ordonnancier.parity.test.js` fails the build on the first drift
 * (PK-BUG-15).
 *
 * The client checks are a courtesy (a file refused before it is sent); the server decides the type again
 * by the file's leading bytes, never by its name or the browser's Content-Type.
 */

/** Prisma `TypePieceOrdonnancier` — the kinds of a dispensation attachment. */
export const TYPE_PIECE_ORDONNANCIER = Object.freeze({
  PIECE_IDENTITE: "PIECE_IDENTITE",
  ORDONNANCE: "ORDONNANCE",
});
export const TYPE_PIECE_ORDONNANCIER_VALUES = Object.freeze(Object.values(TYPE_PIECE_ORDONNANCIER));

/** French labels of the kinds (the server's `TYPES_PIECE_ORDONNANCIER_LIBELLES`). */
export const TYPES_PIECE_ORDONNANCIER_LIBELLES = Object.freeze({
  PIECE_IDENTITE: "Pièce d'identité",
  ORDONNANCE: "Ordonnance",
});

/** États of an attachment as the API names them (`ordonnancier-pieces.service.js` `STATUT_PIECE`). */
export const STATUT_PIECE_ORDONNANCIER = Object.freeze({
  EN_ATTENTE: "EN_ATTENTE",
  LIEE: "LIEE",
  RETIREE: "RETIREE",
});

/** The multipart field of an upload (multipart only — never base64 in JSON). */
export const CHAMP_PIECES_ORDONNANCIER = "fichiers";

/** The accepted types: the clinical signatures (PDF, JPEG, PNG) and the extension each is stored with. */
export const MIMES_PIECE_ORDONNANCIER = Object.freeze(["application/pdf", "image/jpeg", "image/png"]);
export const EXTENSIONS_PIECE_ORDONNANCIER = Object.freeze({
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/png": ".png",
});

/**
 * The bounds (server `PIECES_ORDONNANCIER_BORNES`, platform-governed): 5 Mo per file (the clinical cap
 * `DOCUMENT_BLOB_MAX_BYTES` default), 5 files per request, 10 live attachments per entry, a withdrawal
 * reason of 200 characters.
 */
export const PIECES_ORDONNANCIER_BORNES = Object.freeze({
  max_taille_octets: 5 * 1024 * 1024,
  max_fichiers_requete: 5,
  max_pieces_par_ligne: 10,
  motif_retrait_max: 200,
});

/** Bounds of a dispensation correction (server `ORDONNANCIER_DISPENSATION_BORNES`). */
export const ORDONNANCIER_DISPENSATION_BORNES = Object.freeze({
  duree_jours_min: 1,
  duree_jours_max: 365,
  nom_porteur_max: 160,
  carte_porteur_max: 60,
});

/** The fields a PROPRIÉTAIRE / MANAGER may correct on an entry (`PATCH /ordonnancier/:id`). */
export const CHAMPS_DISPENSATION_CORRIGEABLES = Object.freeze([
  "date_soins",
  "date_delivrance",
  "duree_jours",
  "nom_porteur",
  "carte_porteur",
]);

/** Their French names (server `LIBELLES_CHAMPS_DISPENSATION`). */
export const LIBELLES_CHAMPS_DISPENSATION = Object.freeze({
  date_soins: "Date de soins",
  date_delivrance: "Date de délivrance",
  duree_jours: "Durée",
  nom_porteur: "Porteur",
  carte_porteur: "Pièce du porteur",
});

/** The journal's server sort whitelist (server `ORDONNANCIER_SORT_FIELDS`). */
export const TRIS_JOURNAL_ORDONNANCIER = Object.freeze(["date_ordonnance", "no_ordre", "created_at", "date_delivrance"]);

/** The statuses of a register entry (server `STATUT_ORDONNANCIER`, the list validator's enum). */
export const STATUT_ORDONNANCIER = Object.freeze({
  SAISIE: "SAISIE",
  VALIDEE: "VALIDEE",
  SIGNE: "SIGNE",
});
export const STATUT_ORDONNANCIER_LABELS = Object.freeze({
  SAISIE: "Saisie",
  VALIDEE: "Validée",
  SIGNE: "Signée",
});
